<template>
    <div class="bg-white rounded-lg shadow p-4">
        <h3 class="text-sm font-medium text-gray-700 mb-3">섹터 선택</h3>

        <!-- 채권 유형 선택 -->
        <div class="grid grid-cols-4 gap-2 mb-4">
            <button v-for="type in creditTypes" :key="type.value" @click="selectCreditType(type.value)"
                :disabled="isLoading" :class="[
                    'px-2.5 py-1.5 rounded-md text-sm font-medium transition-colors',
                    modelValue === type.value
                        ? 'bg-gray-800 text-white'
                        : isLoading
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
                ]">
                {{ type.label }}
            </button>
        </div>

        <!-- 그룹 필터 드롭다운 -->
        <div v-if="modelValue" class="relative">
            <button @click="toggleGroupDropdown"
                class="dropdown-button w-full px-3 py-2 text-sm border rounded-md bg-white flex items-center justify-between"
                :class="selectedGroupId
                    ? 'border-gray-500 text-gray-700'
                    : 'border-gray-300 text-gray-600'
                    ">
                <span>{{ selectedGroupName || '그룹 전체' }}</span>
                <svg class="w-4 h-4" :class="showGroupDropdown ? 'rotate-180' : ''" fill="none" stroke="currentColor"
                    viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            <!-- 그룹 목록 -->
            <div v-if="showGroupDropdown"
                class="dropdown-content absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                <div class="p-1">
                    <button @click="selectGroup(null)"
                        class="w-full px-3 py-2 text-sm text-left hover:bg-gray-50 rounded flex items-center justify-between">
                        <span>그룹 전체</span>
                        <svg v-if="!selectedGroupId" class="w-4 h-4 text-blue-600" fill="currentColor"
                            viewBox="0 0 20 20">
                            <path
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" />
                        </svg>
                    </button>
                    <button v-for="group in bondGroups" :key="group.id" @click="selectGroup(group)"
                        class="w-full px-3 py-2 text-sm text-left hover:bg-gray-50 rounded flex items-center justify-between">
                        <span>{{ group.name }}</span>
                        <svg v-if="selectedGroupId === group.id" class="w-4 h-4 text-blue-600" fill="currentColor"
                            viewBox="0 0 20 20">
                            <path
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" />
                        </svg>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, watch, onMounted } from 'vue';
import { creditTypeLabel, creditTypeOrder } from '@/utils/marketFlowUtil';
import bondService from '@/service/bondService';

export default {
    name: 'SectorTypeSelector',
    props: {
        modelValue: {
            type: String,
            default: null,
        },
    },
    emits: ['update:modelValue', 'group-selected'],
    setup(props, { emit }) {
        const isLoading = ref(false);
        const showGroupDropdown = ref(false);
        const bondGroups = ref([]);
        const selectedGroupId = ref(null);
        const selectedGroupName = ref('');

        const creditTypes = creditTypeOrder.map(value => ({
            value,
            label: creditTypeLabel[value],
        }));

        const fetchBondGroups = async creditType => {
            try {
                isLoading.value = true;
                const response = await bondService.getBondGroups(creditType);

                // 각 그룹에 issuerIds 필드 추가
                bondGroups.value = response.map(group => ({
                    ...group,
                    issuerIds: group.issuers
                        ? group.issuers.map(issuer => issuer.id)
                        : [],
                }));
            } catch (error) {
                console.error('채권 그룹 조회 실패:', error);
                bondGroups.value = [];
            } finally {
                setTimeout(() => {
                    isLoading.value = false;
                }, 300);
            }
        };

        const selectCreditType = async type => {
            if (isLoading.value) return;

            // 이미 선택된 타입을 다시 클릭하면 선택 해제
            if (props.modelValue === type) {
                emit('update:modelValue', null);
                selectedGroupId.value = null;
                selectedGroupName.value = '';
                bondGroups.value = [];
                emit('group-selected', null);
                return;
            }

            emit('update:modelValue', type);

            // 그룹 선택 초기화
            selectedGroupId.value = null;
            selectedGroupName.value = '';
            emit('group-selected', null);

            // 새로운 채권 유형의 그룹 조회
            await fetchBondGroups(type);
        };

        const selectGroup = group => {
            if (isLoading.value) return;

            selectedGroupId.value = group?.id || null;
            selectedGroupName.value = group?.name || '';
            showGroupDropdown.value = false;

            emit('group-selected', group);
        };

        const toggleGroupDropdown = () => {
            showGroupDropdown.value = !showGroupDropdown.value;
        };

        // 외부 클릭 시 드롭다운 닫기
        onMounted(() => {
            document.addEventListener('click', e => {
                const target = e.target;
                if (
                    !target.closest('.dropdown-button') &&
                    !target.closest('.dropdown-content')
                ) {
                    showGroupDropdown.value = false;
                }
            });
        });

        // 초기 채권 유형이 있으면 그룹 로드
        watch(
            () => props.modelValue,
            newValue => {
                if (newValue && bondGroups.value.length === 0) {
                    fetchBondGroups(newValue);
                }
            },
            { immediate: true }
        );

        return {
            creditTypes,
            isLoading,
            showGroupDropdown,
            bondGroups,
            selectedGroupId,
            selectedGroupName,
            selectCreditType,
            selectGroup,
            toggleGroupDropdown,
        };
    },
};
</script>

<style scoped>
/* 스크롤바 스타일링 */
.overflow-y-auto::-webkit-scrollbar {
    width: 4px;
}

.overflow-y-auto::-webkit-scrollbar-track {
    background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
    background-color: #cbd5e1;
    border-radius: 4px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
    background-color: #94a3b8;
}
</style>
